import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellEditStartReasons,
  GridRowEditStopReasons,
  GridRowModes,
} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

import {
  deleteArtCustomersRow,
  getArtCustomersNewRow,
  getArtCustomersRow,
  getArtSettingsCustomers,
  saveArtCustomersRow,
  saveArtCustomersRowId,
} from '../../../../../packages/common/api';
import CheckboxIconChecked from '../../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../../packages/common/shared-ui/src/icons/DeleteIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SortIcon from '../../../../../packages/common/shared-ui/src/icons/SortIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import { getTranslatedText } from '../../../../../packages/common/utils/getTranslatedText';

// Original class: fieldAutocomplete
const StyledFieldAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => !['gridStyle'].includes(prop),
})(({ theme, gridStyle }) => ({
  ...(gridStyle && {
    height: '100%',
    width: '100%',
  }),
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(gridStyle && {
      minHeight: '40px',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
    },
    ...(gridStyle && {
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      },
    }),
  },
  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },
  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
    ...(gridStyle && {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    }),
  },
  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    ...(gridStyle && {
      border: 'none',
    }),
  },
}));
// Original class: fieldTextGrid
const StyledFieldTextGrid = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '12px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: '10px',
      '&::placeholder': {
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: gridPopper
const StyledGridPopper = styled(Popper)(theme => ({
  zIndex: 90,

  '& .MuiPaper-root.MuiAutocomplete-paper': {
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '& .MuiAutocomplete-noOptions': {
      padding: '8px 16px',
      color: '#212346',
      fontSize: '12px',
      lineHeight: '20px',
    },
    '& .MuiAutocomplete-listbox': {
      padding: '0',
      '& li': {
        padding: '8px 10px',
        whiteSpace: 'normal',
        fontSize: '12px',
        lineHeight: '20px',
        color: '#212346',
        '& .MuiButtonBase-root.MuiCheckbox-root': {
          padding: '2px',
          marginRight: '10px',
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: '#DCDFF4',
        },
      },
      '&::-webkit-scrollbar': {
        width: '2px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro, {
  shouldForwardProp: prop => !['addRowsStyle'].includes(prop),
})(({ theme, addRowsStyle }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    ...(addRowsStyle && { borderRadius: '0' }),
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&.tableGroupGanttHeaderCell': {},
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      backgroundColor: '#fff',
      ...(addRowsStyle
        ? { borderRadius: '16px 16px 0 0' }
        : { borderRadius: '16px' }),
    },
  },
  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const ArtCustomers = () => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (id, row) => {
      if (row.new) {
        const newArray = gridRows.filter(item => item.id !== row.id);
        setGridRows(newArray);
        return;
      }
      try {
        setLoading(true);
        const response = await deleteArtCustomersRow(id);
        if (response) {
          setGridRows(gridRows.filter(row => row.id !== id));
          setLoading(false);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [gridRows],
  );
  const handleEditClick = useCallback(
    async id => {
      const index = gridRows.findIndex(item => item.id === id);
      try {
        const { data: response } = await getArtCustomersRow(id);
        if (response) {
          const updatedObject = {
            ...response,
            id: id,
            customerValue:
              response.customers.find(item => item.isChecked) !== undefined
                ? response.customers.find(item => item.isChecked)
                : null,
            useWorkDaysActValue:
              response.useWorkDaysActs.find(item => item.isChecked) !==
              undefined
                ? response.useWorkDaysActs.find(item => item.isChecked)
                : null,
            useWorkDaysBillValue:
              response.useWorkDaysBills.find(item => item.isChecked) !==
              undefined
                ? response.useWorkDaysBills.find(item => item.isChecked)
                : null,
            billHandlingTimeValue: response.billHandlingTime,
            actHandlingTimeValue: response.actHandlingTime,
          };
          setGridRows([
            ...gridRows.slice(0, index),
            updatedObject,
            ...gridRows.slice(index + 1),
          ]);
          setRowModesModel(oldModel => ({
            ...oldModel,
            [id]: {
              mode: GridRowModes.Edit,
            },
          }));
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [gridRows],
  );
  const handleField = useCallback(
    (data, id, field) => {
      let updatedObject;
      const pos = gridRows.findIndex(item => item.id === id);
      updatedObject = {
        ...gridRows[pos],
        [field]: typeof data === 'string' ? data : data.valueId,
        [`${field}Value`]: data,
      };
      const newFields = [
        ...gridRows.slice(0, pos),
        updatedObject,
        ...gridRows.slice(pos + 1),
      ];
      setGridRows(newFields);
    },
    [gridRows],
  );

  const handleSaveClick = useCallback(
    async (id, row) => {
      const index = gridRows.findIndex(item => item.id === id);
      //const validation = validateRow(row.id);
      //if (!validation) {
      //  return;
      //}
      let response;
      try {
        const data = {
          customer: row.customerValue.valueId,
          billHandlingTime: row.billHandlingTimeValue,
          useWorkDaysBill: row.useWorkDaysBillValue.valueId,
          actHandlingTime: row.actHandlingTimeValue,
          useWorkDaysAct: row.useWorkDaysActValue.valueId,
        };
        if (rowModesModel[id].mode === 'edit' && !row.new) {
          response = await saveArtCustomersRowId(id, data);
        }
        if (row.new) {
          response = response = await saveArtCustomersRow(data);
        }
        if (response) {
          const { data: res } = response;
          const newCols = res.columns.reduce((columnAcc, item, i) => {
            return {
              ...columnAcc,
              [item.fieldId]: item.value,
            };
          }, {});
          const updatedObject = {
            id: res.rowId,
            ...newCols,
          };
          setGridRows([
            ...gridRows.slice(0, index),
            updatedObject,
            ...gridRows.slice(index + 1),
          ]);
          setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
          });
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [gridRows, rowModesModel],
  );

  const formattedColumns = useCallback(() => {
    const smallFields = ['tracker'];
    const columns = gridColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        position: column.position,
        flex: smallFields.includes(column.field) ? 0 : 1,
        width: smallFields.includes(column.field) && 250,
        cellClassName: props =>
          props.cellMode === 'view' ? 'tableCell' : 'tableCell cellEdit',
        editable: true,
        fieldType: column.fieldType,
        //renderCell: props => {
        //  if (props.field === 'confirmation_trackers.url') {
        //    return (
        //      <Box component={'span'} color={'#3448FF'}>
        //        {props.value}
        //      </Box>
        //    );
        //  } else {
        //    {
        //      return props.value;
        //    }
        //  }
        //},
        renderEditCell: props => {
          const cellProps = props;
          if (props.colDef.fieldType === 'list') {
            return (
              <StyledFieldAutocomplete
                gridStyle={true}
                noOptionsText={getLocalString('labels', 'notFound')}
                onChange={(event, newValue) =>
                  handleField(
                    newValue,
                    props.row.id,
                    props.field.substring(props.field.indexOf('.') + 1),
                  )
                }
                options={
                  props.row[
                    `${props.field.substring(props.field.indexOf('.') + 1)}s`
                  ]
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      {...props}
                      key={option.valueId}
                      value={option.valueId}
                      selected={
                        option?.valueId ===
                        cellProps?.row[
                          `${cellProps.field.substring(cellProps.field.indexOf('.') + 1)}Value`
                        ]?.valueId
                      }
                    >
                      {option.valueName}
                    </MenuItem>
                  );
                }}
                value={
                  props.row[
                    `${props.field.substring(props.field.indexOf('.') + 1)}Value`
                  ]
                    ? props.row[
                        `${props.field.substring(props.field.indexOf('.') + 1)}Value`
                      ]
                    : null
                }
                isOptionEqualToValue={(option, value) =>
                  option.valueId === value.valueId
                }
                getOptionLabel={option => option.valueName || ''}
                slots={{
                  popper: props => (
                    <StyledGridPopper
                      {...props}
                      sx={{ maxWidth: '100%!important' }}
                      placement='bottom-start'
                    ></StyledGridPopper>
                  ),
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={getLocalString('labels', 'input')}
                  />
                )}
                popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
              />
            );
          }
          if (props.colDef.fieldType === 'int') {
            return (
              <StyledFieldTextGrid
                autoFocus
                placeholder={getLocalString('labels', 'input')}
                variant='standard'
                value={
                  props.row[
                    `${props.field.substring(props.field.indexOf('.') + 1)}Value`
                  ]
                    ? props.row[
                        `${props.field.substring(props.field.indexOf('.') + 1)}Value`
                      ]
                    : ''
                }
                onChange={event =>
                  handleField(
                    event.target.value,
                    props.row.id,
                    props.field.substring(props.field.indexOf('.') + 1),
                  )
                }
              ></StyledFieldTextGrid>
            );
          }
        },
      };
    });
    return [
      ...columns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        headerClassName: 'actions',
        width: 70,
        cellClassName: 'actions tableCell',
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            if (id !== '0') {
              return [
                <GridActionsCellItem
                  onClick={() => handleSaveClick(id, row)}
                  icon={<SubmitIcon />}
                  label='Save'
                />,
                <GridActionsCellItem
                  onClick={() => handleDelete(id, row)}
                  icon={<DeleteIconBig />}
                  label='Cancel'
                />,
              ];
            } else {
              return [
                <GridActionsCellItem
                  onClick={() => handleSaveClick(id, row)}
                  icon={<SubmitIcon />}
                  label='Save'
                />,
              ];
            }
          } else {
            if (id !== '0') {
              return [
                <GridActionsCellItem
                  onClick={() => handleEditClick(id)}
                  icon={
                    <UserNewEditIcon
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                    />
                  }
                  label='Edit'
                />,
                <GridActionsCellItem
                  onClick={() => handleDelete(id, row)}
                  icon={<DeleteIconBig />}
                  label='Delete'
                  color='inherit'
                />,
              ];
            } else {
              return [
                <GridActionsCellItem
                  onClick={() => handleEditClick(id)}
                  icon={
                    <UserNewEditIcon
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                    />
                  }
                  label='Edit'
                />,
              ];
            }
          }
        },
      },
    ];
  }, [
    gridColumns,
    handleDelete,
    handleEditClick,
    handleField,
    handleSaveClick,
    getLocalString,
    rowModesModel,
  ]);
  const columnsValue = formattedColumns();
  const formattedRows = useCallback(data => {
    return data.reduce((acc, row) => {
      const newCols = row.columns.reduce((columnAcc, _, i) => {
        return {
          ...columnAcc,
          [row.columns[i].fieldId]: row.columns[i].value,
          [row.columns[i].fieldId + 'Id']: row.columns[i].valueId,
        };
      }, {});
      return [...acc, { ...newCols, id: row.rowId }];
    }, []);
  }, []);

  useEffect(() => {
    let ignore = false;
    async function getArtCustomers() {
      const { data: trackers } = await getArtSettingsCustomers();
      if (!ignore) {
        return trackers;
      }
    }
    getArtCustomers()
      .then(res => {
        setGridColumns(res.fields);
        setGridRows(formattedRows(res.rows));
        setLoading(false);
      })
      .catch(error => {
        handleError(error.response);
      });
    return () => {
      ignore = true;
    };
  }, [handleError, formattedRows]);

  const addRowHandler = useCallback(async () => {
    try {
      const { data: response } = await getArtCustomersNewRow();
      if (response) {
        //const rowColumns = gridColumns.map(item => item.field);
        setGridRows(oldRows => [
          ...oldRows,
          {
            new: true,
            id: `row-${gridRows.length + 1}`,
            ...response,
            customerValue:
              response.customers.find(item => item.isChecked) !== undefined
                ? response.customers.find(item => item.isChecked)
                : null,
            useWorkDaysActValue:
              response.useWorkDaysActs.find(item => item.isChecked) !==
              undefined
                ? response.useWorkDaysActs.find(item => item.isChecked)
                : null,
            useWorkDaysBillValue:
              response.useWorkDaysBills.find(item => item.isChecked) !==
              undefined
                ? response.useWorkDaysBills.find(item => item.isChecked)
                : null,
            billHandlingTimeValue: response.billHandlingTime,
            actHandlingTimeValue: response.actHandlingTime,
          },
        ]);
        setRowModesModel(oldModel => ({
          ...oldModel,
          [`row-${gridRows.length + 1}`]: {
            mode: GridRowModes.Edit,
          },
        }));
      }
    } catch (error) {
      console.error('ERROR WITH GET', error);
    }
  }, [gridRows.length]);

  const handleRowModesModelChange = useCallback(newRowModesModel => {
    setRowModesModel(newRowModesModel);
  }, []);

  const handleRowEditStop = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  }, []);
  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const updatedRow = { ...newRow, isNew: false };
      setGridRows(
        gridRows.map(row => (row.id === newRow.id ? updatedRow : row)),
      );
      return updatedRow;
    },
    [gridRows],
  );
  return (
    <Box
      sx={{
        marginTop: '16px',
        borderRadius: '16px',
        backgroundColor: gridColumns.length > 0 ? '#fff' : 'tranparent',
        minHeight: loading ? '184px' : 'auto',
        position: 'relative',
      }}
    >
      {loading && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 99,
            top: 0,
            position: 'absolute',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      {gridColumns.length > 0 && (
        <>
          <StyledDataGridTableRoot
            addRowsStyle={true}
            localeText={{
              noRowsLabel: getLocalString('labels', 'noData'),
            }}
            onRowEditStart={(params, event) => {
              if (params.reason === GridCellEditStartReasons.printableKeyDown) {
                event.defaultMuiPrevented = true;
              }
              if (params.reason === GridCellEditStartReasons.cellDoubleClick) {
                event.defaultMuiPrevented = true;
              }
            }}
            autoHeight
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            columns={columnsValue}
            rows={gridRows}
            getRowClassName={params => !params.row.active && 'disabled'}
            slots={{
              columnUnsortedIcon: () => (
                <SortIcon width='20' height='20' viewBox='0 0 20 20' />
              ),
              columnSortedAscendingIcon: () => (
                <SortIcon
                  direction='up'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                />
              ),
              columnSortedDescendingIcon: () => (
                <SortIcon
                  direction='down'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                />
              ),
            }}
            getRowHeight={() => 'auto'}
            editMode='row'
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
          />
          <Grid
            sx={{
              padding: '8px 16px',
              lineHeight: 'normal',
              borderRadius: '0 0 16px 16px',
              borderTop: gridRows.length > 0 ? 'none' : '1px solid #E4E4EF',
              backgroundColor: '#fff',
            }}
          >
            <IconButton
              onClick={addRowHandler}
              sx={{ padding: '0' }}
              disabled={gridRows.filter(item => item.new).length > 0}
            >
              <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
            </IconButton>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ArtCustomers;
